// SPDX-License-Identifier: LicenseRef-Proprietary

import { useState, useEffect, useRef, useCallback, RefObject } from 'react';
import { CLOSE_ANIMATION_DURATION_MS } from '../constants';

export interface UseDrawerStateResult {
  isOpen: boolean;
  isClosing: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export interface UseDrawerStateOptions {
  triggerRef: RefObject<HTMLButtonElement>;
  onClose?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
}

export function useDrawerState(options: UseDrawerStateOptions): UseDrawerStateResult {
  const { triggerRef, onClose, onOpenChange } = options;
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Notify parent of open state changes
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Prevent background scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Close handler with animation
  const close = useCallback(() => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    
    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      closeTimeoutRef.current = null;
      // Return focus to bubble button
      triggerRef.current?.focus();
      // Call optional onClose callback
      onClose?.();
    }, CLOSE_ANIMATION_DURATION_MS);
  }, [triggerRef, onClose]);

  // Open handler
  const open = useCallback(() => {
    setIsOpen(true);
    setIsClosing(false);
  }, []);

  // Toggle handler
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, close, open]);

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, close]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    isOpen,
    isClosing,
    open,
    close,
    toggle,
  };
}

